import type { CriminalSubmittedDocumentView } from '../../services/criminalCaseRequests';
import { formatDocumentTableDateTime } from '../../utils/dateFormat';
import { displayClientFileName } from '../../utils/fileDisplayName';

export { formatFileSizeLabel } from '../../utils/fileSize';

export function formatDocUploadedAt(iso?: string): string {
  return formatDocumentTableDateTime(iso) || '—';
}

export function submittedDocUiStatus(doc: CriminalSubmittedDocumentView): 'uploaded' | 'pending' | 'not-uploaded' {
  const s = doc.status.toLowerCase();
  if (s.includes('uploaded') && !s.includes('not')) return 'uploaded';
  if (doc.fileName.trim()) return 'uploaded';
  if (doc.reason.trim()) return 'pending';
  if (s.includes('pending') || s.includes('not_uploaded') || s.includes('not uploaded') || s.includes('not')) {
    return 'pending';
  }
  return 'not-uploaded';
}

/** User-facing file label from documents API (`file_name` / `original_file_name`). */
export function displayDocumentFileName(doc: {
  originalFileName: string | null;
  fileName?: string;
}): string {
  return displayClientFileName(doc.originalFileName, doc.fileName);
}

export type SubmittedDocumentDisplayRow = {
  doc: CriminalSubmittedDocumentView;
  showDocumentType: boolean;
  rowKey: string;
};

/** True when the row has a storage key the download API can use. */
export function hasDownloadableDocumentFile(doc: { fileName?: string }): boolean {
  return Boolean(doc.fileName?.trim());
}

function submittedDocumentGroupKey(doc: CriminalSubmittedDocumentView): string {
  if (doc.documentTypeId.trim()) return doc.documentTypeId.trim();
  if (doc.documentTypeName.trim()) return `name:${doc.documentTypeName.trim()}`;
  return doc.id;
}

/** Groups rows with the same document type; type label is shown only on the first row per group. */
export function buildSubmittedDocumentDisplayRows(
  submitted: CriminalSubmittedDocumentView[],
): SubmittedDocumentDisplayRow[] {
  const order: string[] = [];
  const groups = new Map<string, CriminalSubmittedDocumentView[]>();

  for (const doc of submitted) {
    const key = submittedDocumentGroupKey(doc);
    if (!groups.has(key)) {
      order.push(key);
      groups.set(key, []);
    }
    groups.get(key)!.push(doc);
  }

  const rows: SubmittedDocumentDisplayRow[] = [];
  for (const key of order) {
    const group = groups.get(key) ?? [];
    group.forEach((doc, index) => {
      const rowKey =
        doc.id && doc.fileName.trim()
          ? `${doc.id}:${doc.fileName.trim()}`
          : `${key}:${index}:${doc.fileName.trim() || 'empty'}`;
      rows.push({ doc, showDocumentType: index === 0, rowKey });
    });
  }
  return rows;
}